import React, { useState, useEffect, useContext } from 'react';
import Select from 'react-select';
import { PatientIDContext } from '../../Contexts';
import { isTrashedTrial } from '../../util/activeTrials';
import { getAllTrialsByUser } from '../../util/reportAPI';

function trialSelectStyles(fontSize) {
    return {
        container: (baseStyle) => ({
            ...baseStyle,
            margin: 0,
            minWidth: 0,
            width: 'max-content',
        }),
        control: (baseStyle) => ({
            ...baseStyle,
            border: 'none',
            outline: 'none',
            boxShadow: 'none',
            cursor: 'pointer',
            fontSize,
            backgroundColor: 'transparent',
            minHeight: 0,
            height: 'auto',
            width: 'max-content',
            alignItems: 'center',
        }),
        valueContainer: (baseStyle) => ({
            ...baseStyle,
            padding: '0 8px',
        }),
        input: (baseStyle) => ({
            ...baseStyle,
            margin: 0,
            padding: 0,
        }),
        dropdownIndicator: (baseStyle) => ({
            ...baseStyle,
            color: '#63C7CF',
            cursor: 'pointer',
            padding: '0 2px 0 0',
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
        singleValue: (baseStyle) => ({
            ...baseStyle,
            fontSize,
            lineHeight: 1.2,
            position: 'relative',
            top: 'auto',
            transform: 'none',
            margin: 0,
            maxWidth: 'none',
        }),
        menu: (baseStyle) => ({
            ...baseStyle,
            fontSize,
            zIndex: 1300,
            width: 'max-content',
            minWidth: '100%',
        }),
        menuList: (baseStyle) => ({
            ...baseStyle,
            padding: 0,
        }),
        menuPortal: (baseStyle) => ({ ...baseStyle, zIndex: 1300 }),
        option: (baseStyle) => ({
            ...baseStyle,
            fontSize,
            lineHeight: 1.2,
            padding: '8px',
            whiteSpace: 'nowrap',
        }),
    };
}

function TrialDropDown(props) {
    const {
        handleChange,
        selectedOption,
        fontSize = '1.25rem',
        className,
        loadTrials = getAllTrialsByUser,
    } = props;
    const patientID = useContext(PatientIDContext);
    const [trials, setTrials] = useState([]);

    useEffect(() => {
        let cancelled = false;
        // Recordings already loads /a/get-trials-by-user, which omits trashed rows.
        // /a/get-trials-by-patient used to return those same rows, so the picker
        // listed trials that had been moved to trash.
        loadTrials()
            .then(res => {
                if (cancelled) return;
                const next = ((res && res.result) || [])
                    .filter((trial) => trial && String(trial.patient_id) === String(patientID))
                    .filter((trial) => !isTrashedTrial(trial))
                    .map((trial) => ({
                        value: trial.id,
                        label: trial.id == null ? '' : String(trial.id),
                    }));
                next.sort((a, b) => {
                    const left = Number(a.value);
                    const right = Number(b.value);
                    if (Number.isFinite(left) && Number.isFinite(right) && left !== right) {
                        return right - left;
                    }
                    return String(b.value).localeCompare(String(a.value), undefined, { numeric: true });
                });
                setTrials(next);
            });
        return () => {
            cancelled = true;
        };
    }, [patientID, loadTrials])

    const selected = selectedOption == null || selectedOption === ''
        ? undefined
        : trials.filter(option => option.value != null && option.value.toString() === selectedOption.toString())[0];

    return (
        <section className="container trial-dropdown-wrap" style={{ padding: 0, margin: 0, minWidth: 0 }}>
            <Select
                placeholder={"Please select a trial to analyze"}
                isSearchable={false}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                styles={trialSelectStyles(fontSize)}
                className={["trial", "select-dropdown", className].filter(Boolean).join(" ")}
                options={trials}
                onChange={option => option ? handleChange(option.value) : handleChange("")}
                value={selected}
            />
        </section>
    )
}

export default TrialDropDown;
